/**
 * CTA arrival attribution (#595) — belt-and-suspenders for cta_click.
 *
 * The primary credit is the widget's pre-navigation cta_click POST, which
 * can lose to the unload race or to any presentation path that dropped the
 * slug. So every presented CTA URL ALSO carries `bhai_ctaslug=<slug>`: the
 * destination page's widget detects the param on arrival, posts the same
 * cta_click (via:"arrival"), and strips the param from the address bar.
 * Server-side dedup per (session, slug) collapses a double credit.
 *
 * Pure module (no zod) — the widget snippet (bh.js) imports it and the
 * bundle-integrity gate bans zod there.
 */

export const CTA_SLUG_PARAM = "bhai_ctaslug";

/** Append the attribution param. Idempotent; preserves existing query and
 *  hash; relative URLs resolve against `base` when given. An unparseable
 *  URL returns unchanged — a CTA must never break because of tracking. */
export function stampCtaUrl(url: string, slug: string, base?: string): string {
  if (!url || !slug) return url;
  try {
    const u = base ? new URL(url, base) : new URL(url);
    if (u.searchParams.get(CTA_SLUG_PARAM) === slug) return url;
    u.searchParams.set(CTA_SLUG_PARAM, slug);
    return u.href;
  } catch {
    return url;
  }
}

/** The slug carried by an arrival URL's query string, or null. */
export function extractCtaArrival(search: string): string | null {
  try {
    const slug = new URLSearchParams(search).get(CTA_SLUG_PARAM);
    return slug && slug.trim() ? slug : null;
  } catch {
    return null;
  }
}

/** The href with the attribution param removed (for history.replaceState —
 *  the param must not survive into copied links or re-fire on reload). */
export function stripCtaParam(href: string): string {
  try {
    const u = new URL(href);
    if (!u.searchParams.has(CTA_SLUG_PARAM)) return href;
    u.searchParams.delete(CTA_SLUG_PARAM);
    return u.href;
  } catch {
    return href;
  }
}

// ── Product links ───────────────────────────────────────────────────────────
//
// These live HERE, beside the param they ride on, rather than in widget-api where
// they started: the snippet needs the prefix to tell a product arrival from a CTA
// arrival, and the snippet cannot import widget-api. Defining the prefix on both
// sides would be the duplicated-contract defect — one half renames it, the other
// keeps crediting the wrong event type, and nothing fails to compile.

/**
 * Prefix that marks a stamped slug as a PRODUCT link rather than a CTA.
 *
 * Product links reuse `bhai_ctaslug` deliberately — the widget already reads that
 * param on arrival, strips it from the address bar and credits the click, and a
 * second parameter would mean a second copy of all of that to keep in step. What
 * it cannot do is resolve the value against the `ctas` table, because a product is
 * not a CTA row. The prefix is how the arrival path tells the two apart and emits
 * `product_click` instead of `cta_click`.
 */
export const PRODUCT_SLUG_PREFIX = "p_";

/** The slug a product's link carries. Deterministic from the product id: a click
 *  joins back to the recommendation through (session, product), so there is
 *  nothing per-turn to mint or store. */
export function productCtaSlug(productId: string): string {
  return `${PRODUCT_SLUG_PREFIX}${productId}`;
}

/** The product id a stamped slug refers to, or null when the slug is a CTA's. */
export function productIdFromSlug(slug: string): string | null {
  return slug.startsWith(PRODUCT_SLUG_PREFIX) ? slug.slice(PRODUCT_SLUG_PREFIX.length) || null : null;
}

/**
 * The product id carried by a STAMPED HREF, or null.
 *
 * Takes a full or relative href rather than a search string, because the click path
 * sees an anchor's `href` while the arrival path sees `location.search`. Returns
 * null for a CTA slug, an unstamped link, or anything unparseable — a link must
 * never fail to navigate because of attribution.
 */
export function productIdFromStampedUrl(href: string, base?: string): string | null {
  try {
    const u = new URL(href, base ?? "http://x.invalid");
    const slug = u.searchParams.get(CTA_SLUG_PARAM);
    return slug ? productIdFromSlug(slug) : null;
  } catch {
    return null;
  }
}
